import React from 'react';
import styled from 'styled-components';

import MetaTags from 'components/meta-tags';
import SignUpForm from 'components/forms/sign-up-form';
import SvgChevronIcon from 'src/assets/chevron-icon';
import type { IrlEvent } from 'src/gql/graphql';
import { Card, CardFooter } from './card';
import {
  BackButton,
  ContentHeader,
  FooterText,
  FooterLink,
  FormWrapper,
  Description,
  Heading,
  Content,
} from './styles';
import { IrlEventLogo } from './irl-event-logo';
import { useIrlEventParams } from '../hooks/use-irl-event-params';

type IrlEventEmailSignupProps = {
  irlEvent: IrlEvent;
  onSignIn: () => void;
  onSignupSuccess: () => void;
  onBack?: () => void;
};

export const IrlEventEmailContentHeader = (): JSX.Element => (
  <ContentHeader>
    <Heading>Almost there! Set up your account</Heading>
    <Description>Choose how you would like to sign up and start earning rewards today!</Description>
  </ContentHeader>
);

export function IrlEventEmailSignup({
  irlEvent,
  onSignIn,
  onSignupSuccess,
  onBack,
}: IrlEventEmailSignupProps): JSX.Element {
  const { publicContextSessionId } = useIrlEventParams();

  const footer = (
    <CardFooter>
      <FooterText>
        Already have an account?{' '}
        <FooterLink onClick={onSignIn} data-testid='irl-event-signup-sign-in-link'>
          Log in
        </FooterLink>
      </FooterText>
    </CardFooter>
  );

  return (
    <>
      <MetaTags title='Create Your Account' description='Sign up to join the event' />

      <Card footer={footer} data-testid='irl-event-email-signup'>
        <IrlEventLogo headerImageUrl={irlEvent.headerImageUrl ?? null} />

        <IrlEventEmailContentHeader />

        <Content>
          <FormWrapper>
            <SignUpForm
              onClose={onSignupSuccess}
              analyticsEventLabel='irl-event-signup'
              displayLoginLink={false}
              publicContextSessionId={publicContextSessionId}
              submitButtonLabel='Create Account'
              skipPostSignupSequence
            />
            {onBack && (
              <StyledBackButton type='button' onClick={onBack} data-testid='irl-event-signup-back-button'>
                <StyledChevronIcon height={9} width={15} />
                Back
              </StyledBackButton>
            )}
          </FormWrapper>
        </Content>
      </Card>
    </>
  );
}

const StyledBackButton = styled(BackButton)`
  margin: -28px 0 10px;
`;

const StyledChevronIcon = styled(SvgChevronIcon)`
  transform: rotate(90deg);
`;
